/**
 * Per-brand persistence for the Mailbox, mock state.
 *
 * The section's mailboxes and entitlement live in a component-local reducer, discarded when the
 * operator navigates away. To make the operator's own changes survive navigation AND a reload, the
 * reducer's data is mirrored here to localStorage, keyed per brand, and rehydrated on mount. The
 * per-brand mock seed stays the baseline: an untouched brand resolves to its seed (so brand-
 * switching still demos every state), while a brand the operator has changed resolves to their set.
 *
 * OWN KEY. This file persists under its own localStorage key, distinct from any other section's, so
 * sections never see each other's mailboxes and deleting one leaves the others' storage untouched
 * (the two sibling mail sections built alongside this one have since been deleted).
 *
 * WHAT IS PERSISTED: mailboxes + entitlement + the section's domain, together, in ONE record per
 * brand. They change together (create/remove touch `mailboxes`, `entitlement.used` and, on the
 * first create or the last delete, the domain's status), so a single atomic write keeps them from
 * ever drifting apart across a crash between two writes.
 *
 * WHAT IS NOT PERSISTED: `secrets` (the plaintext login passwords) is DELIBERATELY absent here. The
 * real provider returns a password once, at create and at each reset, and never again; "visible
 * this session only" is the credential's real behaviour, so writing it to localStorage would both
 * misrepresent that and leave a live password at rest on disk. It lives only in the in-memory
 * reducer and dies with the tab. See `state.ts` -> `MailboxesState.secrets`.
 *
 * This is the mock persistence layer. When the real provider endpoints land, swap these reads and
 * writes for the API (or a Redux slice) and delete this file with the rest of the mock layer.
 */
import {
  type MailboxDomain,
  type MailboxRecord,
  type MailboxEntitlement,
  MAILBOX_STATE_KEY,
  readMailboxBrandRecords,
  loadMailboxBrandRecord,
  resolveInitialMailboxes,
  type MailboxPersistedMap,
} from '../../mock-data/mailbox-records';
import { MAILBOX_LEDGER_KEYS } from '../../mock-data/mailbox-wallet';

// The READ side (the key, the validated record read, the seed variant and the mailbox resolution)
// lives in the neutral `mock-data/mailbox-records.ts`, because the Home page must resolve exactly
// the rows this section does without importing this folder. This file keeps the WRITE side, the
// entitlement resolution and the wallet ledger. Re-exported so this folder's callers keep one import.
export { resolveInitialMailboxes };

const STORE_KEY = MAILBOX_STATE_KEY;
type PersistedMap = MailboxPersistedMap;
const readAll = readMailboxBrandRecords;
const loadBrandRecord = loadMailboxBrandRecord;

const writeAll = (map: PersistedMap): void => {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch {
    // Storage unavailable (private mode / blocked): the in-session reducer still holds the state.
  }
};

/**
 * Persist a brand's mailboxes, entitlement AND domain in one atomic write. The reducer keeps
 * `used`, the row set and the domain's walk back consistent; writing them together preserves that
 * on disk (a two-write scheme could crash between the two and desync `used` from the count
 * forever). Takes the fields by name, never the whole reducer state, so `secrets` cannot reach
 * disk by accident.
 */
export const saveBrandMailboxState = (
  brandId: string,
  mailboxes: MailboxRecord[],
  entitlement: MailboxEntitlement,
  mailDomain: MailboxDomain | null,
): void => {
  const all = readAll();
  all[brandId] = { mailboxes, entitlement, mailDomain };
  writeAll(all);
};

/**
 * The domain to start a brand from: the saved one when the section has changed it (a finished DNS
 * poll, a walk back), else the brand's seed domain. A record saved before the domain was persisted
 * has none (`undefined`) and resolves to the seed; a saved `null` is a domain the service has never
 * heard of and stays `null`.
 */
export const resolveInitialMailDomain = (
  brandId: string | undefined,
  seedDomain: MailboxDomain | null,
): MailboxDomain | null => {
  const saved = brandId ? loadBrandRecord(brandId)?.mailDomain : undefined;
  return saved === undefined ? seedDomain : saved;
};

/** The operator's saved mailboxes for a brand, or `null` when they have never changed it. */
export const loadBrandMailboxes = (brandId: string): MailboxRecord[] | null =>
  loadBrandRecord(brandId)?.mailboxes ?? null;

/** The operator's saved entitlement for a brand, or `null` when they have never changed it. */
export const loadBrandEntitlement = (brandId: string): MailboxEntitlement | null =>
  loadBrandRecord(brandId)?.entitlement ?? null;

/** Whether a brand has any saved record at all (drives the persist effect's "seed vs saved" test). */
export const hasBrandMailboxRecord = (brandId: string | undefined): boolean =>
  brandId ? loadBrandRecord(brandId) !== null : false;

/**
 * The entitlement to start a brand from. If the brand has a saved record we take the saved
 * entitlement, but `used` is RECOMPUTED from the resolved mailbox count: `included` is the server's
 * free allowance (safe to trust), while `used` must equal the number of LIVE mailboxes or
 * `nextMailboxIsPaid` prices the next one wrong. Recomputing here heals any half-written or
 * hand-edited localStorage. With no saved record, `included` comes from the seed and `used` from the
 * seed's own row count, which agree by construction.
 */
export const resolveInitialEntitlement = (
  brandId: string | undefined,
  seedEntitlement: MailboxEntitlement,
  resolvedMailboxes: MailboxRecord[],
): MailboxEntitlement => {
  const saved = brandId ? loadBrandEntitlement(brandId) : null;
  const included = saved?.included ?? seedEntitlement.included;
  // A `deleted` row the provider still lists is not a seat: it holds no allowance. Nor is support@
  // (role `system`): it is the brand's, not one of the operator's.
  return { included, used: resolvedMailboxes.filter((m) => m.status !== 'deleted' && m.role !== 'system').length };
};

// --- Dev-only wallet debit ledger (see mock-data/mailbox-wallet.ts) -----------------------------

/**
 * What each brand has spent locally on mailboxes, in whole cents, so `28.6` three times is
 * exactly `8580` on disk and never a floating-point tail. This section's OWN key, declared by the
 * neutral mock-wallet module (which the wallet slice reads), so the section writes the key and the
 * slice reads the owner-level total without importing this page folder.
 */
const WALLET_SPENT_KEY = MAILBOX_LEDGER_KEYS.mailbox;

type SpentMap = Record<string, number>;

const readAllSpent = (): SpentMap => {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(WALLET_SPENT_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: SpentMap = {};
    for (const [brandId, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value) && value >= 0) out[brandId] = Math.round(value);
    }
    return out;
  } catch {
    return {};
  }
};

/**
 * What a brand has spent locally on mailboxes, in major units. Zero when it has never spent,
 * and when storage is blocked or unreadable: the section then shows the real balance in full, which
 * is the safe failure (nothing real was charged either way).
 */
export const loadMailboxWalletSpent = (brandId: string): number => {
  const all = readAllSpent();
  return Object.prototype.hasOwnProperty.call(all, brandId) ? all[brandId] / 100 : 0;
};

/** Persist a brand's local spend (major units in, whole cents on disk). Never throws. */
export const saveMailboxWalletSpent = (brandId: string, value: number): void => {
  try {
    if (typeof window === 'undefined') return;
    const all = readAllSpent();
    all[brandId] = Math.max(0, Math.round(value * 100));
    window.localStorage.setItem(WALLET_SPENT_KEY, JSON.stringify(all));
  } catch {
    // Storage unavailable: the in-session ledger still holds until the next load.
  }
};

/**
 * Add one settlement to a brand's local spend and return the new total, in major units. Done in
 * whole cents so `28.6` three times is exactly `85.8`. The single place a wallet settlement lands
 * on disk; the purchase hook calls it and nothing else writes the ledger.
 */
export const addMailboxWalletSpent = (brandId: string, amount: number): number => {
  const next = Math.round(loadMailboxWalletSpent(brandId) * 100) + Math.round(amount * 100);
  saveMailboxWalletSpent(brandId, next / 100);
  return next / 100;
};
